import { useContext, useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import { PHONE_NUM_REGEX } from "@src/utility/helpers/regex";
import { UserContext } from "@context/user";
import Validations from "@helpers/validations";
import { object } from "yup";
import useAxios from "@hooks/use-axios";
import useImageUpload from "@src/views/common/hooks/use-upload-image";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    name     : "",
    email    : "",
    platforms: [],
    role     : "",
    status   : "",
    title    : "",
    phone    : "",
    region   : {
        timezone: ""
    }
};

const useAccount = () => {

    const { loading, axiosInstance, cancelAllRequests } = useAxios({
        base         : "panel",
        initialLoader: true
    });
    const { loading: updateLoading, axiosInstance: upsertInstance } = useAxios({
        base: "panel"
    });

    const { userData:{ user:{ _id:userId } = {} } = {} } = useContext(UserContext);
    
    const [userDetails, setUserDetails] = useState({});

    const {
        requiredValidation,
        emailRequiredValidation,
        arrayMinMaxLengthValidation,
        minMaxRequiredString,
        requiredRegexValidation
    } = Validations();

    const validationSchema = object().shape({
        name     : requiredValidation({ name: "Name" }),
        email    : emailRequiredValidation({ name: "Email" }),
        role     : requiredValidation({ name: "Role" }),
        status   : requiredValidation({ name: "Status" }),
        title    : minMaxRequiredString({ name: "Title" }),
        phone    : requiredRegexValidation({ regex: PHONE_NUM_REGEX, name: "Phone" }),
        platforms: arrayMinMaxLengthValidation({ name: "Products", min: 1 }),
        region   : object().shape({
            timezone: requiredValidation({ name: "Timezone" })
        })
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const {
        handleImageSelect,
        setImage,
        image,
        loading: uploadLoading
    } = useImageUpload({
        methods,
        url: "/user/profile-picture"
    });
    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    const getUserDetails = async () => {
        const { data } = await axiosInstance.get(
            `/user/profile/${userId}`
        );
        setImage(data.pp);
        setUserDetails({
            ...data,
            platforms: data.platforms.map((item) => ({
                label: item.name,
                value: item.ID
            }))
        });
        reset({
            ...data,
            platforms: data.platforms.map((item) => ({
                label: item.name,
                value: item.ID
            }))
        });
    };

    useEffect(() => {
        if (userId) {
            getUserDetails();
        }
        return () => {
            cancelAllRequests();
        };
    }, [userId]);

    const onSubmit = () => {
        handleSubmit(async (data) => {
            const payload = {
                name     : data.name,
                email    : data.email,
                platforms: data.platforms?.map((item) => item.value),
                role     : data.role,
                status   : data.status,
                title    : data.title,
                phone    : data.phone,
                region   : {
                    timezone: data.region.timezone
                }
            };
            await upsertInstance.patch(`/user/${userId}`, payload);
            getUserDetails();
        })();
    };

    const handleCancel = () => {
        reset(userDetails);
    };
    return {
        loading,
        isDirty,
        onSubmit,
        handleCancel,
        methods,
        updateLoading,
        image,
        handleImageSelect,
        userDetails,
        uploadLoading
    };
};

export default useAccount;
